import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { useReportContext } from "../../../report-context";

import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useCreateFinalReport, useGetReportColumns } from "@/queries";
import { displayErrorToast } from "@/utils/toast";
import { ColumnType, ReportColumn } from "@/types/evaluate";
import { useReportCells } from "@/components/Evaluate/hooks/useReportCells";

export const useEditBlueprint = () => {
  const reportContext = useReportContext();
  const auth = useAuth();
  const userContext = useUser();
  const navigate = useNavigate();
  const userToken = auth!.userToken!;

  const { reportId } = reportContext;

  // Fetch report columns
  useGetReportColumns(userToken, reportId, data => {
    reportContext.setReportColumns(data);
  });

  // Fetch report cells
  useReportCells({});

  const { mutateAsync: createFinalReport, isLoading: createFinalReportIsLoading } =
    useCreateFinalReport(userToken);

  const handleBuildReport = useCallback(
    async (reportName: string) => {
      if (reportId) {
        const finalReportName = `${reportName}`;

        try {
          const response = await createFinalReport({
            reportId: reportId,
            name: finalReportName,
          });

          if (response.success) {
            const finalReportId = response.report_id;
            if (finalReportId) {
              navigate(
                `/workspace/${userContext?.activeWorkspaceId}/evaluate/blueprints/${reportId}/reports/${finalReportId}`,
              );
            }
          } else {
            displayErrorToast(response.message || "There was an error creating the final report");
          }
        } catch (error) {
          displayErrorToast(
            typeof error === "string" ? error : "An error occurred while creating the final report",
          );
        }
      }
    },
    [reportId, createFinalReport, navigate, userContext],
  );

  // Computed values
  const blueprintMetadata = reportContext.reportMetadata?.report;
  const sortedBlueprintColumns = reportContext.sortedColumns;
  const blueprintColumnsIsLoading = reportContext.reportColumnsIsLoading;
  const blueprintDatasetInfo = reportContext.datasetInfo;
  const blueprintCells = reportContext.reportCells;

  const datasetRows = blueprintDatasetInfo ? blueprintDatasetInfo.total : 0;
  const previewCellCount = blueprintCells?.length;
  const previewColumnCount = sortedBlueprintColumns?.filter(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (c: any) => c.id && c.id !== -999,
  )?.length;
  const previewRows =
    previewCellCount && previewColumnCount ? Math.round(previewCellCount / previewColumnCount) : 0;

  const hasAddedNonDatasetColumns = !!sortedBlueprintColumns?.some(
    (column: ReportColumn) => column.column_type !== ColumnType.DATASET && column.id !== -999,
  );

  const hiddenRows = Math.floor(datasetRows - previewRows > 0 ? datasetRows - previewRows : 0);

  return {
    reportId,
    blueprintMetadata,
    sortedBlueprintColumns,
    blueprintColumnsIsLoading,
    blueprintDatasetInfo,
    blueprintCells,
    handleBuildReport,
    createFinalReportIsLoading,
    hasAddedNonDatasetColumns,
    hiddenRows,
    reportContext,
  };
};
